import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccountingOrgBar from "./AccountingOrgBar";
import { canLoadAccountingData } from "../useAccountingScope";
import { currencyFormatter } from "utilities/currencyFormatter";

const emptyManualLine = () => ({ account_id: "", debit: "", credit: "", memo: "" });

const JournalEntriesTab = ({
	requiresOrganizationSelection,
	sessionOrganization,
	organizationId,
	setOrganizationId,
	eventTypes,
	fetchAccounts,
	fetchJournalEntries,
	createManualJournalEntry,
	isSubmitting,
}) => {
	const [entries, setEntries] = useState([]);
	const [accounts, setAccounts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [eventFilter, setEventFilter] = useState("");
	const [expandedId, setExpandedId] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState({
		entry_date: new Date().toISOString().slice(0, 10),
		description: "",
		reference: "",
		lines: [emptyManualLine(), { ...emptyManualLine(), credit: "" }],
	});

	const canLoad = canLoadAccountingData(requiresOrganizationSelection, organizationId);

	const load = async () => {
		if (!canLoad) {
			setEntries([]);
			return;
		}
		setLoading(true);
		setError("");
		const filters = {};
		if (requiresOrganizationSelection) filters.organization_id = organizationId;
		if (eventFilter) filters.event_type = eventFilter;
		const [entriesResult, accountsResult] = await Promise.all([
			fetchJournalEntries(filters),
			fetchAccounts(organizationId),
		]);
		if (entriesResult.success) setEntries(entriesResult.journalEntries);
		else setError(entriesResult.message);
		if (accountsResult.success) setAccounts(accountsResult.accounts);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [organizationId, eventFilter, requiresOrganizationSelection]);

	const postableAccounts = accounts.filter((a) => a.is_postable !== false && a.is_active !== false);

	const updateLine = (index, patch) => {
		setForm((f) => ({
			...f,
			lines: f.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
		}));
	};

	const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyManualLine()] }));
	const removeLine = (index) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== index) }));

	const handleManualSubmit = async () => {
		if (!form.description.trim()) {
			setMessage("Description is required.");
			return;
		}
		const lines = form.lines.map((line) => ({
			account_id: Number(line.account_id),
			debit: Number(line.debit) || 0,
			credit: Number(line.credit) || 0,
			memo: line.memo || "",
		}));
		if (lines.some((l) => !l.account_id)) {
			setMessage("Each line needs an account.");
			return;
		}
		setMessage("");
		const payload = {
			entry_date: form.entry_date,
			description: form.description,
			reference: form.reference,
			lines,
		};
		if (requiresOrganizationSelection) {
			payload.organization_id = organizationId;
		}
		const result = await createManualJournalEntry(payload);
		if (result.success) {
			setDialogOpen(false);
			load();
		} else {
			setMessage(result.message);
		}
	};

	const formatMoney = (val) => {
		const n = parseFloat(val);
		if (!n) return "—";
		return currencyFormatter(n);
	};

	return (
		<Box>
			<Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
				<AccountingOrgBar
					requiresOrganizationSelection={requiresOrganizationSelection}
					organizationId={organizationId}
					onOrganizationChange={setOrganizationId}
					sessionOrganization={sessionOrganization}
				/>
				<FormControl size="small" sx={{ minWidth: 200 }}>
					<InputLabel>Event type</InputLabel>
					<Select label="Event type" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}>
						<MenuItem value="">All</MenuItem>
						{eventTypes.map((et) => (
							<MenuItem key={et.event_type} value={et.event_type}>{et.label || et.event_type}</MenuItem>
						))}
					</Select>
				</FormControl>
				<Button variant="outlined" onClick={load} disabled={!canLoad}>Refresh</Button>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => setDialogOpen(true)}
					disabled={!canLoad || isSubmitting}
				>
					Manual entry
				</Button>
			</Box>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			{message && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessage("")}>{message}</Alert>}

			{!canLoad ? (
				<Typography color="text.secondary">Select an organisation.</Typography>
			) : loading ? (
				<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
			) : (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell width={40} />
								<TableCell>Entry #</TableCell>
								<TableCell>Date</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>Event</TableCell>
								<TableCell>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{entries.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} align="center">No journal entries.</TableCell>
								</TableRow>
							) : (
								entries.map((row) => (
									<React.Fragment key={row.id}>
										<TableRow hover>
											<TableCell>
												<IconButton size="small" onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}>
													{expandedId === row.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
												</IconButton>
											</TableCell>
											<TableCell sx={{ fontFamily: "monospace" }}>{row.entry_number}</TableCell>
											<TableCell>{row.entry_date}</TableCell>
											<TableCell>{row.description}</TableCell>
											<TableCell sx={{ fontFamily: "monospace" }}>{row.event_type || "—"}</TableCell>
											<TableCell>{row.status}</TableCell>
										</TableRow>
										<TableRow>
											<TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
												<Collapse in={expandedId === row.id}>
													<Box sx={{ py: 1, pl: 4 }}>
														{(row.lines || []).map((line) => (
															<Typography key={line.id} variant="body2" sx={{ fontFamily: "monospace" }}>
																{line.account_code} {line.account_name} — Dr {formatMoney(line.debit)} / Cr {formatMoney(line.credit)}
																{line.memo ? ` (${line.memo})` : ""}
															</Typography>
														))}
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									</React.Fragment>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle>Manual journal entry</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<TextField type="date" label="Entry date" value={form.entry_date} onChange={(e) => setForm((f) => ({ ...f, entry_date: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
						<TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth />
						<TextField label="Reference" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} fullWidth />
						<Typography variant="subtitle2">Lines (debits must equal credits)</Typography>
						{form.lines.map((line, index) => (
							<Box key={index} display="flex" gap={1} flexWrap="wrap" alignItems="center">
								<FormControl size="small" sx={{ minWidth: 200 }}>
									<InputLabel>Account</InputLabel>
									<Select label="Account" value={line.account_id} onChange={(e) => updateLine(index, { account_id: e.target.value })}>
										{postableAccounts.map((a) => (
											<MenuItem key={a.id} value={a.id}>{a.code} — {a.name}</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField size="small" label="Debit" type="number" value={line.debit} onChange={(e) => updateLine(index, { debit: e.target.value })} sx={{ width: 120 }} />
								<TextField size="small" label="Credit" type="number" value={line.credit} onChange={(e) => updateLine(index, { credit: e.target.value })} sx={{ width: 120 }} />
								<TextField size="small" label="Memo" value={line.memo} onChange={(e) => updateLine(index, { memo: e.target.value })} sx={{ flex: 1, minWidth: 100 }} />
								<IconButton size="small" onClick={() => removeLine(index)} disabled={form.lines.length <= 2}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Box>
						))}
						<Button size="small" onClick={addLine}>Add line</Button>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={handleManualSubmit} disabled={isSubmitting}>
						{isSubmitting ? "Posting…" : "Post entry"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default JournalEntriesTab;
